import React, { useState } from "react";
import { useFavorites } from "../../context/FavoritesContext";
import PropertySummaryModal from "../modals/PropertySummaryModal";
import MapSidebar from "../modals/MapSidebar";
import ZillowModal from "../modals/ZillowModal";

const Favorites = () => {
  const { favorites, removeFavorite, updateFavoriteNotes, clearFavorites, loading } =
    useFavorites();
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [mapSidebarOpen, setMapSidebarOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [zillowModalOpen, setZillowModalOpen] = useState(false);
  const [zillowUrl, setZillowUrl] = useState("");
  const [zillowPropertyAddress, setZillowPropertyAddress] = useState("");
  const [editingNotes, setEditingNotes] = useState({});
  const [selectMode, setSelectMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  // Function to handle address click for map view
  const handleAddressClick = (address, city, state) => {
    const fullAddress = `${address}, ${city}, ${state}`;
    setSelectedAddress(fullAddress);
    setMapSidebarOpen(true);
  };

  // Function to handle Zillow link click
  const handleZillowClick = (address, city, state) => {
    const fullAddress = `${address}, ${city}, ${state}`;
    const zillowLink = `https://www.zillow.com/homes/${encodeURIComponent(
      fullAddress
    )}`;
    setZillowUrl(zillowLink);
    setZillowPropertyAddress(fullAddress);
    setZillowModalOpen(true);
  };

  // Function to handle AI summary button click
  const handleSummaryClick = (property) => {
    setSelectedProperty(property);
    setSummaryModalOpen(true);
  };

  // Function to close map sidebar
  const closeMapSidebar = () => {
    setMapSidebarOpen(false);
  };

  // Function to close Zillow modal
  const closeZillowModal = () => {
    setZillowModalOpen(false);
    // Clear the Zillow URL after the modal animation completes
    setTimeout(() => {
      setZillowUrl("");
      setZillowPropertyAddress("");
    }, 300);
  };

  // Function to handle notes editing
  const handleNotesChange = (propertyId, notes) => {
    setEditingNotes({
      ...editingNotes,
      [propertyId]: notes,
    });
  };

  // Function to save notes
  const saveNotes = (propertyId) => {
    if (editingNotes[propertyId] !== undefined) {
      updateFavoriteNotes(propertyId, editingNotes[propertyId]);

      // Clear the editing state for this property
      const newEditingNotes = { ...editingNotes };
      delete newEditingNotes[propertyId];
      setEditingNotes(newEditingNotes);
    }
  };

  // Function to toggle select mode
  const toggleSelectMode = () => {
    setSelectMode(!selectMode);
    setSelectedItems([]);
  };

  // Function to toggle item selection
  const toggleItemSelection = (propertyId) => {
    setSelectedItems((prev) => {
      if (prev.includes(propertyId)) {
        return prev.filter((id) => id !== propertyId);
      } else {
        return [...prev, propertyId];
      }
    });
  };

  // Function to remove selected items
  const removeSelectedItems = () => {
    selectedItems.forEach((id) => removeFavorite(id));
    setSelectedItems([]);
    setSelectMode(false);
  };

  // Function to format date string
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) return 'N/A';
      
      const options = { year: "numeric", month: "short", day: "numeric" };
      return date.toLocaleDateString(undefined, options);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          My Favorite Properties
        </h1>
        <div className="flex space-x-3">
          <button
            onClick={toggleSelectMode}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md"
          >
            {selectMode ? "Cancel Selection" : "Select Multiple"}
          </button>
          {selectMode && selectedItems.length > 0 && (
            <button
              onClick={removeSelectedItems}
              className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md"
            >
              Remove Selected ({selectedItems.length})
            </button>
          )}
          {Array.isArray(favorites) && favorites.length > 0 && !selectMode && (
            <button
              onClick={() => {
                if (
                  window.confirm(
                    "Are you sure you want to clear all favorites?"
                  )
                ) {
                  clearFavorites();
                }
              }}
              className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md"
            >
              Clear All
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="bg-white p-8 rounded-lg shadow text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your favorites...</p>
        </div>
      ) : !Array.isArray(favorites) || favorites.length === 0 ? (
        <div className="bg-white p-8 rounded-lg shadow text-center">
          <svg
            className="mx-auto h-16 w-16 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            No favorites yet
          </h3>
          <p className="mt-1 text-gray-500">
            Start adding properties to your favorites list from the property
            listings.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {Array.isArray(favorites) && favorites.map((property) => {
            const propertyId = property.id || property.property_id;
            const isEditing = editingNotes[propertyId] !== undefined;
            const isSelected = selectedItems.includes(propertyId);

            return (
              <div
                key={propertyId}
                className={`relative bg-white rounded-lg shadow-md overflow-hidden border-2 ${
                  isSelected ? "border-blue-500" : "border-transparent"
                }`}
              >
                {selectMode && (
                  <div className="absolute top-2 left-2 z-10">
                    <input
                      type="checkbox"
                      className="h-5 w-5 text-blue-600 rounded"
                      checked={isSelected}
                      onChange={() => toggleItemSelection(propertyId)}
                    />
                  </div>
                )}

                <div className="p-5">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-1">
                        {property.address}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {property.city}, {property.state}
                      </p>
                    </div>
                    {!selectMode && (
                      <button
                        onClick={() => removeFavorite(propertyId)}
                        className="text-red-500 hover:text-red-700"
                        title="Remove from favorites"
                      >
                        <svg
                          className="h-5 w-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    )}
                  </div>

                  <div className="mt-4 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">
                        Upset Value:
                      </span>
                      <span className="text-sm font-medium">
                        {property.upset_value || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">
                        Zillow Estimate:
                      </span>
                      <span className="text-sm font-medium">
                        {property.marketValueFormatted || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">
                        Profit Potential:
                      </span>
                      <span
                        className={`text-sm font-medium ${
                          property.profit > 50000
                            ? "text-green-600"
                            : property.profit > 25000
                            ? "text-blue-600"
                            : property.profit > 0
                            ? "text-yellow-600"
                            : "text-red-600"
                        }`}
                      >
                        {property.profitFormatted || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Sale Date:</span>
                      <span className="text-sm font-medium">
                        {formatDate(property.sale_date)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">
                        Added to Favorites:
                      </span>
                      <span className="text-sm text-gray-500">
                        {formatDate(property.favorited_at)}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-gray-700">Notes</h4>
                      {!isEditing ? (
                        <button
                          onClick={() =>
                            handleNotesChange(propertyId, property.notes)
                          }
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          Edit
                        </button>
                      ) : (
                        <button
                          onClick={() => saveNotes(propertyId)}
                          className="text-sm text-green-600 hover:text-green-800"
                        >
                          Save
                        </button>
                      )}
                    </div>

                    {!isEditing ? (
                      <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded min-h-[60px]">
                        {property.notes || "No notes added yet."}
                      </p>
                    ) : (
                      <textarea
                        value={editingNotes[propertyId]}
                        onChange={(e) =>
                          handleNotesChange(propertyId, e.target.value)
                        }
                        className="w-full text-sm p-2 border border-gray-300 rounded"
                        rows={3}
                        placeholder="Add notes about this property..."
                      />
                    )}
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      onClick={() =>
                        handleAddressClick(
                          property.address,
                          property.city,
                          property.state
                        )
                      }
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 mr-1"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      Map
                    </button>

                    <button
                      onClick={() =>
                        handleZillowClick(
                          property.address,
                          property.city,
                          property.state
                        )
                      }
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 mr-1"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                      Zillow
                    </button>

                    <button
                      onClick={() => handleSummaryClick(property)}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-gradient-to-r from-purple-500 to-purple-700 hover:from-purple-600 hover:to-purple-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 mr-1"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                        />
                      </svg>
                      Analyze
                    </button>

                    {property.property_id && (
                      <a
                        href={`https://salesweb.civilview.com/Sales/SaleDetails?PropertyId=${property.property_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-gray-700 bg-gray-200 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 mr-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        Record
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Property summary modal */}
      {selectedProperty && (
        <PropertySummaryModal
          isOpen={summaryModalOpen}
          property={selectedProperty}
          loading={false}
          onClose={() => setSummaryModalOpen(false)}
        />
      )}

      {/* Map sidebar */}
      <MapSidebar
        isOpen={mapSidebarOpen}
        address={selectedAddress}
        onClose={closeMapSidebar}
      />

      {/* Zillow modal */}
      <ZillowModal
        isOpen={zillowModalOpen}
        zillowUrl={zillowUrl}
        propertyAddress={zillowPropertyAddress}
        onClose={closeZillowModal}
      />
    </div>
  );
};

export default Favorites;
